import {ReactElement, Ref, forwardRef, useRef} from 'react';
import styles from './HorizontalList.module.scss';
import {setRefs} from '../../commonSub/function';
import useStopPropagationOnScroll from '../hook/useStopPropagationOnScroll';

interface IProps<T, K> {
  items: T[];
  keyName?: K;
  containerPaddingSize?: string; // 사용하는 곳의 부모 가로 패딩 값, px단위
  children: (item: T, index: number) => ReactElement;
  itemWrapperClassName?: string;
  paddingElement?: ReactElement;
}

function HorizontalList<K extends string, T extends {[key in K | number]: any}>(
  {
    items,
    keyName,
    containerPaddingSize = '20px',
    children,
    itemWrapperClassName,
    paddingElement,
  }: IProps<T, K>,
  ref: Ref<HTMLDivElement>
) {
  const listRef = useRef<HTMLDivElement>(null);
  useStopPropagationOnScroll(listRef);

  const paddingSizeStyle = {flex: `0 0 ${containerPaddingSize}`};
  return (
    <div
      ref={el => setRefs(el, ref, listRef)}
      className={cn(styles.horizontalList, 'flex overflow-x-auto no-scrollbar')}
      style={
        paddingElement
          ? {}
          : {
              marginRight: `-${containerPaddingSize}`,
              marginLeft: `-${containerPaddingSize}`,
            }
      }
    >
      {paddingElement || <div style={paddingSizeStyle} />}
      {items.map((item, index) => {
        const isLast = items.length === index + 1;
        return (
          <div
            key={keyName ? item[keyName] : index}
            className={cn(
              itemWrapperClassName,
              isLast ? 'mr-0' : 'mr-4',
              'flex-none'
            )}
          >
            {children(item, index)}
          </div>
        );
      })}
      {paddingElement || <div style={paddingSizeStyle} />}
    </div>
  );
}

export default forwardRef(HorizontalList) as <
  K extends string,
  T extends {[key in K | number]: any},
>(
  props: IProps<T, K> & {ref?: Ref<HTMLDivElement | null>}
) => ReactElement;
